'use client';

import { useId, useState } from 'react';
import { LayoutGroup, motion } from 'framer-motion';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { resolveI18nText } from '@/sanity/globals/I18n';
import {
  IPricingCategory,
  IPricingSectionV2,
  IPricingSpecDefinition,
  IPricingTier,
  IPricingTierCTA,
  IPricingTierFee,
  IPricingTierSpec,
} from '@/sanity/lib/definitions';
import { IPricingV2Discount } from '@/sanity/templates';
import {
  Tooltip,
  TooltipTrigger,
  TooltipCustomContent,
  TooltipProvider,
} from '@/shadcn/ui/tooltip';
import { CTAButton } from '@/shadcn/ui/button';
import {
  applyDiscountToPrice,
  getSpecDefinition,
  isSpecOnActiveTab,
  resolveSpecTabValue,
  resolveTierDiscountPercent,
  TIER_THEME_STYLES,
  TierTheme,
  tierCtaToGroqButton,
} from './helpers';

const ease = [0.32, 0.72, 0, 1] as const;
const FLIP_TRANSITION = 'transform 0.72s cubic-bezier(0.4, 0.08, 0.2, 1)';

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="none"
      className={cn('shrink-0 w-[2rem] h-[2rem]', className)}
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1" fill="none" />
      <circle cx="8" cy="5" r="0.9" fill="currentColor" />
      <rect x="7.15" y="7" width="1.7" height="5" rx="0.85" fill="currentColor" />
    </svg>
  );
}

function SpecsToggle({
  primaryLabel,
  secondaryLabel,
  secondaryActive,
  onChange,
  layoutId,
}: {
  primaryLabel?: string;
  secondaryLabel?: string;
  secondaryActive: boolean;
  onChange: (v: boolean) => void;
  layoutId: string;
}) {
  return (
    <LayoutGroup id={layoutId}>
      <div className="flex items-center w-full p-[0.5rem] md:p-[0.75rem] rounded-[0.5rem] border border-black/10 bg-[#f4f4f4]">
        {(
          [
            { key: 'primary' as const, label: primaryLabel, active: !secondaryActive },
            { key: 'secondary' as const, label: secondaryLabel, active: secondaryActive },
          ]
        ).map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key === 'secondary')}
            className="relative flex-1 flex items-center justify-center px-[1.5rem] py-[1rem] md:px-[2.75rem] md:py-[1.25rem] focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40 rounded-[0.25rem]"
            aria-pressed={tab.active}
          >
            {tab.active && (
              <motion.span
                layoutId={`${layoutId}-indicator`}
                className="absolute inset-0 rounded-[0.25rem] border border-black/[0.14] bg-black"
                transition={{ type: 'spring', bounce: 0.18, duration: 0.5 }}
              />
            )}
            <motion.span
              className="relative z-10 block text-[1.75rem] md:text-[2.25rem] leading-[1.3] tracking-[-0.035rem] md:tracking-[-0.045rem] font-geist font-medium whitespace-nowrap"
              animate={{ color: tab.active ? '#ffffff' : 'rgba(0,0,0,0.4)' }}
              transition={{ duration: 0.28, ease }}
            >
              {tab.label}
            </motion.span>
          </button>
        ))}
      </div>
    </LayoutGroup>
  );
}

function SpecRow({
  label,
  tooltip,
  value,
}: {
  label?: string;
  tooltip?: string;
  value?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex w-full items-center justify-between gap-[1rem]">
      <div className="flex items-center gap-[0.75rem] md:gap-[1rem] min-w-0">
        <p className="mb-0 text-[1.75rem] md:text-[2rem] leading-[normal] font-geist font-normal text-black opacity-[0.62]">
          {label}
        </p>
        {tooltip ? (
          <TooltipProvider delayDuration={100}>
            <Tooltip open={open} onOpenChange={setOpen}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="flex items-center shrink-0 text-black/40 hover:text-black/70 transition-colors cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpen((o) => !o);
                  }}
                  aria-label={`${label} info`}
                >
                  <InfoIcon />
                </button>
              </TooltipTrigger>
              <TooltipCustomContent
                side="top"
                className="max-w-[24rem] rounded-[0.375rem] bg-[#103037] px-[1.25rem] py-[0.75rem] text-[1.75rem] font-geist leading-[1.4] text-white"
              >
                {tooltip}
              </TooltipCustomContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-black/40">
            <InfoIcon />
          </span>
        )}
      </div>
      <p className="mb-0 text-[1.75rem] md:text-[2rem] leading-[1.5] font-geist font-medium text-black opacity-80 whitespace-nowrap text-right">
        {value || '—'}
      </p>
    </div>
  );
}

function TierFace({
  tier,
  category,
  assetKey,
  specsCatalog,
  language,
  isFundedFace,
  hasFundedState,
  secondaryActive,
  onSecondaryActiveChange,
  onFlip,
  faceHidden,
  discount,
  sharedFlipToFundedLabel,
  sharedFlipToFundedLabelI18n,
  sharedFlipBackLabel,
  sharedFlipBackLabelI18n,
}: {
  tier: IPricingTier;
  category: IPricingCategory;
  assetKey?: string;
  specsCatalog?: IPricingSpecDefinition[];
  language?: string;
  isFundedFace: boolean;
  hasFundedState: boolean;
  secondaryActive: boolean;
  onSecondaryActiveChange: (v: boolean) => void;
  onFlip: () => void;
  faceHidden: boolean;
  discount?: IPricingV2Discount;
  sharedFlipToFundedLabel?: string;
  sharedFlipToFundedLabelI18n?: IPricingSectionV2['flipToFundedLabelI18n'];
  sharedFlipBackLabel?: string;
  sharedFlipBackLabelI18n?: IPricingSectionV2['flipBackLabelI18n'];
}) {
  const toggleId = useId();
  const theme = (tier.theme ?? 'light-green') as TierTheme;
  const styles = TIER_THEME_STYLES[theme];

  const label = resolveI18nText(tier.label, tier.labelI18n, language);
  const amount = resolveI18nText(tier.amount, tier.amountI18n, language);

  const challengeSubLabel = resolveI18nText(category.subLabel, category.subLabelI18n, language);
  const fundedSubLabel = resolveI18nText(tier.fundedSubLabel, tier.fundedSubLabelI18n, language);
  const activeSubLabel = isFundedFace ? fundedSubLabel : challengeSubLabel;

  const toggle = tier.specsToggle;
  const primaryToggleLabel = resolveI18nText(toggle?.primaryLabel, toggle?.primaryLabelI18n, language);
  const secondaryToggleLabel = resolveI18nText(toggle?.secondaryLabel, toggle?.secondaryLabelI18n, language);

  const useSecondaryFee =
    !!tier.hasSpecsToggle && secondaryActive && !!(tier.secondaryFee?.current || tier.secondaryFee?.original);
  const fee: IPricingTierFee | undefined = useSecondaryFee ? tier.secondaryFee : tier.fee;
  const feeSuffix = resolveI18nText(fee?.suffix, fee?.suffixI18n, language);

  // Runtime discount from pricingV2Discount global (feedback item #4).
  // If a % is set for this (asset, category), we treat fee.original as the base
  // and compute both current and label from it. If no global % is set, fall
  // back to whatever fee.current / discountLabel are manually written.
  const globalDiscountPercent = resolveTierDiscountPercent(assetKey, category.key, discount);
  const rawOriginal = resolveI18nText(fee?.original, fee?.originalI18n, language);
  const rawCurrent = resolveI18nText(fee?.current, fee?.currentI18n, language);
  const rawDiscountLabel = resolveI18nText(fee?.discountLabel, fee?.discountLabelI18n, language);

  const usingGlobalDiscount = !!globalDiscountPercent && !!rawOriginal;
  const feeOriginal = usingGlobalDiscount ? rawOriginal : rawOriginal;
  const feeCurrent = usingGlobalDiscount
    ? applyDiscountToPrice(rawOriginal, globalDiscountPercent!)
    : rawCurrent;
  const feeDiscount = usingGlobalDiscount ? `-${globalDiscountPercent}%` : rawDiscountLabel;

  // The two specs-toggle tabs are different products (e.g. Futures Classic vs
  // Classic DLL — different price, different SKU), so the secondary tab needs its
  // own checkout link. Falls back to the primary CTA when none is set.
  const useSecondaryCta =
    !!tier.hasSpecsToggle && secondaryActive && !!tier.secondaryCtaPrimary?.href;
  const challengeCta = useSecondaryCta ? tier.secondaryCtaPrimary : tier.ctaPrimary;

  const activeCta: IPricingTierCTA | undefined = isFundedFace ? tier.fundedCtaPrimary : challengeCta;
  const ctaTitle = resolveI18nText(activeCta?.title, activeCta?.titleI18n, language);
  const ctaButtonData = tierCtaToGroqButton(activeCta, ctaTitle);

  // Section-level labels (edited once by marketing) take precedence over
  // per-tier legacy values; tier values remain as fallback.
  const flipToFundedLabel =
    resolveI18nText(sharedFlipToFundedLabel, sharedFlipToFundedLabelI18n, language) ||
    resolveI18nText(tier.flipToFundedLabel, tier.flipToFundedLabelI18n, language) ||
    'After You Pass →';
  const flipBackLabel =
    resolveI18nText(sharedFlipBackLabel, sharedFlipBackLabelI18n, language) ||
    resolveI18nText(tier.flipBackLabel, tier.flipBackLabelI18n, language) ||
    '← Back to Challenge';
  const flipButtonLabel = isFundedFace ? flipBackLabel : flipToFundedLabel;

  const activeSpecsSource: IPricingTierSpec[] =
    (isFundedFace ? tier.fundedSpecs : tier.specs) ?? [];

  const visibleSpecs = activeSpecsSource.filter(
    (spec) => !!spec.key && isSpecOnActiveTab(spec, tier, secondaryActive),
  );

  return (
    <div
      style={{
        gridArea: '1 / 1',
        position: 'relative',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        transform: isFundedFace ? 'rotateY(180deg)' : 'rotateY(0deg)',
      }}
      aria-hidden={faceHidden}
      className={cn(
        'flex flex-col border border-black/10 pb-[2rem] bg-white overflow-hidden',
        faceHidden && 'pointer-events-none',
      )}
    >
      <div className="flex flex-col gap-[2.75rem] md:gap-[3.75rem]">
        {/* Header + optional toggle group */}
        <div className="flex flex-col gap-[1.125rem]">
          {/* Header */}
          <div className="pt-[1rem] px-[1rem]">
            <div
              className={cn(
                'relative flex w-full items-center overflow-clip rounded-[0.25rem] px-[2.25rem] py-[2rem] md:px-[3rem] md:py-[2.5rem]',
                styles.headerBg,
              )}
            >
              {styles.headerImage && (
                <Image
                  src={styles.headerImage}
                  width={styles.headerImage.width}
                  height={styles.headerImage.height}
                  alt=""
                  aria-hidden
                  className={cn(
                    'pointer-events-none absolute right-0 bottom-0 !w-auto !h-full max-w-none',
                    styles.headerImageBlend,
                  )}
                />
              )}
              <div className="relative z-10 flex flex-col gap-[4rem] md:gap-[6rem]">
                <div className="flex items-center gap-[0.75rem] shrink-0 flex-wrap">
                  {label && (
                    <span
                      className={cn(
                        'inline-flex items-center justify-center rounded-[0.25rem] px-[1rem] py-[0.5rem] text-[1.75rem] md:text-[2rem] leading-[1.2] tracking-[-0.035rem] md:tracking-[-0.04rem] font-geist font-normal text-white whitespace-nowrap capitalize',
                        styles.tierChipBg,
                      )}
                    >
                      {label.toLowerCase()}
                    </span>
                  )}
                  {activeSubLabel && (
                    <span
                      className={cn(
                        'inline-flex items-center justify-center rounded-[0.25rem] border px-[1rem] py-[0.5rem] text-[1.75rem] md:text-[2rem] leading-[1.3] font-geist font-normal whitespace-nowrap',
                        styles.categoryChipBorder,
                        styles.categoryChipText,
                      )}
                    >
                      {activeSubLabel}
                    </span>
                  )}
                </div>
                <p
                  className={cn(
                    'mb-0 text-[5.5rem] md:text-[6.75rem] leading-[1.2] tracking-[-0.3rem] md:tracking-[-0.4725rem] font-geist font-normal whitespace-nowrap',
                    styles.headerText,
                  )}
                >
                  {amount}
                </p>
              </div>
            </div>
          </div>

          {/* Optional specs toggle */}
          {tier.hasSpecsToggle && toggle && (
            <div className="px-[1rem]">
              <SpecsToggle
                primaryLabel={primaryToggleLabel}
                secondaryLabel={secondaryToggleLabel}
                secondaryActive={secondaryActive}
                onChange={onSecondaryActiveChange}
                layoutId={`specs-toggle-${toggleId}`}
              />
            </div>
          )}
        </div>

        {/* Specs list */}
        <div className="relative px-[1.5rem] md:px-[2rem]">
          <div className="flex flex-col gap-[1.125rem] md:gap-[1.25rem]">
            {visibleSpecs.map((spec) => {
              const def = getSpecDefinition(specsCatalog, spec.key);
              const specLabel = resolveI18nText(def?.label, def?.labelI18n, language);
              const tooltip = resolveI18nText(def?.tooltip, def?.tooltipI18n, language);
              const tabValue = resolveSpecTabValue(spec, tier, secondaryActive);
              const value = resolveI18nText(tabValue.value, tabValue.valueI18n, language);

              return (
                <div key={spec._key ?? spec.key} className="flex w-full flex-col gap-[1.25rem]">
                  <SpecRow label={specLabel} tooltip={tooltip} value={value} />
                  <div className="h-[1px] w-full bg-black/10" aria-hidden="true" />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Fee + CTAs */}
      <div className="mt-[3.5rem] md:mt-[4.75rem] flex flex-col gap-[3rem] md:gap-[4.625rem] px-[1.5rem] md:px-[2rem]">
        <div className="flex flex-col gap-[1.25rem] md:gap-[1.75rem]">
          {(feeOriginal || feeDiscount) && (
            <div className="flex items-center gap-[0.75rem] flex-wrap">
              {feeOriginal && (
                <span className="mb-0 text-[2rem] md:text-[2.5rem] leading-[1.35] tracking-[-0.06rem] md:tracking-[-0.075rem] font-geist font-medium text-black line-through opacity-[0.43]">
                  {feeOriginal}
                </span>
              )}
              {feeDiscount && (
                <span className="inline-flex items-center justify-center rounded-[3.125rem] bg-[#1aa87f] px-[1rem] py-[0.5rem] md:py-[0.75rem] text-[1.75rem] md:text-[2rem] leading-[1.35] font-geist font-medium text-white whitespace-nowrap">
                  {feeDiscount}
                </span>
              )}
            </div>
          )}
          {feeCurrent && (
            <div className="flex items-end gap-[0.5rem] flex-wrap">
              <span className="mb-0 text-[4.5rem] md:text-[5.5rem] leading-[0.9] tracking-[-0.135rem] md:tracking-[-0.165rem] font-geist font-semibold text-[#003828] whitespace-nowrap">
                {feeCurrent}
              </span>
              {feeSuffix && (
                <span className="mb-0 text-[1.5rem] md:text-[1.75rem] leading-[1.35] tracking-[-0.03rem] md:tracking-[-0.035rem] font-geist font-medium text-black/30 whitespace-nowrap">
                  {feeSuffix}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Buttons group */}
        <div className="flex flex-col gap-[1rem]">
          {hasFundedState && (
            <button
              type="button"
              onClick={onFlip}
              className="w-full inline-flex items-center justify-center min-h-[6rem] rounded-[0.25rem] border border-black/30 bg-white hover:bg-black hover:text-white px-[3.5rem] text-[2.25rem] lg:text-[1.75rem] leading-none tracking-[-0.035rem] md:tracking-[-0.04rem] font-geist font-semibold text-black transition-colors"
              aria-pressed={isFundedFace}
            >
              {flipButtonLabel}
            </button>
          )}

          {ctaButtonData && (
            <div className="w-full">
              <CTAButton data={ctaButtonData} className="w-full" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TierCard({
  tier,
  category,
  assetKey,
  specsCatalog,
  language,
  index,
  isFunded,
  onFlip,
  discount,
  sharedFlipToFundedLabel,
  sharedFlipToFundedLabelI18n,
  sharedFlipBackLabel,
  sharedFlipBackLabelI18n,
}: {
  tier: IPricingTier;
  category: IPricingCategory;
  assetKey?: string;
  specsCatalog?: IPricingSpecDefinition[];
  language?: string;
  index: number;
  isFunded: boolean;
  onFlip: () => void;
  discount?: IPricingV2Discount;
  sharedFlipToFundedLabel?: string;
  sharedFlipToFundedLabelI18n?: IPricingSectionV2['flipToFundedLabelI18n'];
  sharedFlipBackLabel?: string;
  sharedFlipBackLabelI18n?: IPricingSectionV2['flipBackLabelI18n'];
}) {
  const [secondaryActive, setSecondaryActive] = useState(false);

  const hasFundedState = !!tier.fundedSpecs?.length;
  const flipActive = isFunded && hasFundedState;

  const faceProps = {
    tier,
    category,
    assetKey,
    specsCatalog,
    language,
    hasFundedState,
    secondaryActive,
    onSecondaryActiveChange: setSecondaryActive,
    onFlip,
    discount,
    sharedFlipToFundedLabel,
    sharedFlipToFundedLabelI18n,
    sharedFlipBackLabel,
    sharedFlipBackLabelI18n,
  };

  return (
    <motion.div
      className="relative w-full"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease, delay: index * 0.08 }}
    >
      {/*
        Isolate perspective from framer-motion's inline transform.
        iOS Safari flattens preserve-3d when the perspective ancestor
        also has a transform (which motion.div keeps as translate3d
        after whileInView settles), breaking backface-visibility.
      */}
      <div
        className="relative w-full"
        style={{
          perspective: '1600px',
          WebkitPerspective: '1600px',
        }}
      >
        <div
          className="relative w-full grid"
          style={{
            transformStyle: 'preserve-3d',
            WebkitTransformStyle: 'preserve-3d',
            transition: FLIP_TRANSITION,
            WebkitTransition: `-webkit-${FLIP_TRANSITION}`,
            willChange: 'transform',
            transform: flipActive ? 'rotateY(180deg)' : 'rotateY(0deg)',
          }}
        >
          <TierFace {...faceProps} isFundedFace={false} faceHidden={flipActive} />
          {hasFundedState && (
            <TierFace {...faceProps} isFundedFace faceHidden={!flipActive} />
          )}
        </div>
      </div>
    </motion.div>
  );
}
